import { KinetiqStakingAccountantAbi } from '../abis/kinetiq'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Kinetiq kHYPE reader.
 *
 * Two calls: `kHYPE.totalSupply()` + `StakingAccountant
 * .kHYPEToHYPE(1e18)` (returns the 1e18-scaled HYPE-per-kHYPE rate).
 *
 * The kHYPE token contract is a plain ERC-20; the rate-bearing
 * accountant is a separate contract — its address is pinned in
 * `entry.readerExtras.kinetiqAccountant`.
 *
 * Fallback to `offChain`-style placeholder if the accountant is
 * absent (e.g. on a chain without a Kinetiq deployment).
 */
export const readerKinetiqKHype = (entry: LstRegistryEntry): LstReader => {
  const accountant = entry.readerExtras?.kinetiqAccountant
  if (!accountant) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: accountant, name: 'kHYPEToHYPE', params: [ONE_E18] },
    ],
    abis: [TotalSupplyAbi, KinetiqStakingAccountantAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
      }
    },
  }
}
